import React, { useState, useEffect } from 'react';
import { FaUndo, FaSearch, FaCheck, FaTimes, FaEye, FaClock } from 'react-icons/fa';

const SellerReturns = () => {
    const [returns, setReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        // Mock data - in production, fetch from API
        setTimeout(() => {
            setReturns([
                { id: 'RET001', orderId: 'ORD156', customer: 'Meera Devi', product: 'Cotton Handloom Saree', reason: 'Size not matching', status: 'pending', date: '2024-12-11' },
                { id: 'RET002', orderId: 'ORD143', customer: 'Vikram Rao', product: 'Terracotta Vase', reason: 'Product damaged', status: 'approved', date: '2024-12-10' },
                { id: 'RET003', orderId: 'ORD128', customer: 'Lakshmi N', product: 'Silver Anklet', reason: 'Wrong product received', status: 'rejected', date: '2024-12-08' },
            ]);
            setLoading(false);
        }, 500);
    }, []);

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'approved': return 'bg-green-100 text-green-800';
            case 'rejected': return 'bg-red-100 text-red-800';
            case 'completed': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const handleApprove = (id) => {
        setReturns(returns.map(r => r.id === id ? { ...r, status: 'approved' } : r));
    };

    const handleReject = (id) => {
        setReturns(returns.map(r => r.id === id ? { ...r, status: 'rejected' } : r));
    };

    const filteredReturns = filter === 'all' ? returns : returns.filter(r => r.status === filter);

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Returns & Refunds</h1>
                <p className="text-gray-600">Manage return requests from customers</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
                    <p className="text-gray-500 text-sm">Pending</p>
                    <p className="text-2xl font-bold text-gray-800">{returns.filter(r => r.status === 'pending').length}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
                    <p className="text-gray-500 text-sm">Approved</p>
                    <p className="text-2xl font-bold text-gray-800">{returns.filter(r => r.status === 'approved').length}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
                    <p className="text-gray-500 text-sm">Rejected</p>
                    <p className="text-2xl font-bold text-gray-800">{returns.filter(r => r.status === 'rejected').length}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
                    <p className="text-gray-500 text-sm">Total This Month</p>
                    <p className="text-2xl font-bold text-gray-800">{returns.length}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="flex gap-2">
                    {['all', 'pending', 'approved', 'rejected'].map((status) => (
                        <button
                            key={status}
                            onClick={() => setFilter(status)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${filter === status
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Returns List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Loading returns...</div>
                ) : filteredReturns.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">No return requests found</div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Return ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredReturns.map((item) => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{item.id}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-blue-600">{item.orderId}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{item.customer}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{item.product}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-sm">{item.reason}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                                            {item.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {item.status === 'pending' ? (
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleApprove(item.id)}
                                                    className="p-2 text-green-600 hover:bg-green-50 rounded"
                                                    title="Approve"
                                                >
                                                    <FaCheck />
                                                </button>
                                                <button
                                                    onClick={() => handleReject(item.id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                                                    title="Reject"
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        ) : (
                                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                                                <FaEye />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default SellerReturns;
